import clsx from 'clsx';
import React from 'react';
import styles from './AttributeFilterButton.module.css';
import { useMapDisplayContext } from '../MapDisplayContext';
import { useTranslation } from 'react-i18next';
import { getFilteredPins } from '../util';
import { ReduxState } from 'ducks';
import { smoothMoveCamera } from '../mapUtils';
import { useSelector } from 'react-redux';
import { AttributeFilterType, useUrlQueryDisplayState } from '../useUrlQueryDisplayState';
import { useStarredPins } from 'hooks/useStarredPins';

interface AttributeFilterButtonProps {
  action: AttributeFilterType;
}

const AttributeFilterButton: React.FC<AttributeFilterButtonProps> = ({ action }) => {
  const { t } = useTranslation();
  const { filters, setFilters } = useUrlQueryDisplayState();
  const selected = filters.attributes?.includes(action);
  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const { googleMap, openLayersMap } = useMapDisplayContext();

  const { isStarred } = useStarredPins();

  const onClick = () => {
    const newFilters = {
      ...filters,
      attributes: selected
        ? filters.attributes?.filter((a) => a !== action)
        : [...filters.attributes, action],
    };

    // Recenter the map based on pins that match the new filters.
    const filteredPins = getFilteredPins(
      digitalMap?.pins ?? [],
      newFilters,
      '',
      undefined,
      isStarred
    );

    const bounds = new google.maps.LatLngBounds();
    filteredPins.forEach((pin) => {
      if (pin.location) {
        bounds.extend(new google.maps.LatLng(pin.location.latitude, pin.location.longitude));
      }
    });
    const newCenter = bounds.getCenter();

    smoothMoveCamera(googleMap, openLayersMap, {
      center: {
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      },
      zoom: digitalMap?.default_map_zoom ?? 15,
    });

    setFilters(newFilters);
  };

  return (
    <button className={clsx(styles.button, selected && styles.selected)} onClick={onClick}>
      {action === 'hasTickets' ? (
        <svg
          width="19"
          height="17"
          viewBox="0 0 19 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 14.0042C1 14.8303 1.60781 15.5 2.35757 15.5H16.1879C16.9375 15.5 17.5455 14.8303 17.5455 14.0042V11.2523C16.4454 10.9238 15.6363 9.81597 15.6363 8.49999C15.6363 7.18401 16.4454 6.07623 17.5455 5.74768V2.99582C17.5455 2.16971 16.9375 1.5 16.1879 1.5H2.35757C1.60781 1.5 1 2.16971 1 2.99582V5.74144C2.11078 6.06202 2.93029 7.17556 2.93029 8.49999C2.93029 9.82442 2.11078 10.938 1 11.2585V14.0042Z"
            stroke="#393D3F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.1351 1.51172V3.96581"
            stroke="#393D3F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.1351 7.27344V9.72752"
            stroke="#393D3F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.1351 13.043V15.497"
            stroke="#393D3F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : action === 'hasMobileOrders' ? (
        <svg
          width="11"
          height="15"
          viewBox="0 0 11 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="phone-mobile-phone--android-phone-mobile-device-smartphone-iphone">
            <path
              id="Vector"
              d="M8.96143 1H1.96143C1.40915 1 0.961426 1.44771 0.961426 2V13C0.961426 13.5523 1.40915 14 1.96143 14H8.96143C9.51373 14 9.96143 13.5523 9.96143 13V2C9.96143 1.44771 9.51373 1 8.96143 1Z"
              stroke="#393D3F"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              id="Vector_2"
              d="M4.96143 11.5H5.96143"
              stroke="#393D3F"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19"
          height="17"
          viewBox="0 0 19 17"
          fill="none"
        >
          <path
            d="M10.1468 1.62756L12.0644 5.49894C12.108 5.59721 12.1766 5.68232 12.2634 5.74581C12.3501 5.80928 12.452 5.84892 12.5588 5.86075L16.792 6.48789C16.9147 6.50364 17.0302 6.55395 17.1252 6.63293C17.2203 6.7119 17.2909 6.81629 17.3288 6.9339C17.3668 7.05151 17.3704 7.17749 17.3394 7.29712C17.3085 7.41676 17.2441 7.52509 17.1538 7.6095L14.1025 10.6367C14.0247 10.7094 13.9663 10.8005 13.9326 10.9016C13.8988 11.0027 13.891 11.1106 13.9096 11.2156L14.6453 15.4728C14.6666 15.5952 14.6531 15.721 14.6064 15.8361C14.5597 15.9511 14.4817 16.0507 14.3811 16.1237C14.2807 16.1966 14.1616 16.2397 14.0378 16.2484C13.9139 16.257 13.7901 16.2307 13.6804 16.1723L9.86938 14.1582C9.7718 14.1104 9.66453 14.0855 9.55581 14.0855C9.4471 14.0855 9.33984 14.1104 9.24225 14.1582L5.43117 16.1723C5.32153 16.2307 5.19773 16.257 5.07386 16.2484C4.94999 16.2397 4.83101 16.1966 4.73049 16.1237C4.62995 16.0507 4.5519 15.9511 4.5052 15.8361C4.45851 15.721 4.44504 15.5952 4.46634 15.4728L5.20202 11.1673C5.22067 11.0624 5.21278 10.9545 5.17908 10.8534C5.14539 10.7522 5.08693 10.6612 5.00906 10.5884L1.92161 7.6095C1.83027 7.52279 1.76603 7.41144 1.73668 7.28896C1.70733 7.16648 1.71413 7.03811 1.75624 6.91941C1.79836 6.80071 1.874 6.69678 1.97399 6.62018C2.07398 6.5436 2.19403 6.49764 2.3196 6.48789L6.55278 5.86075C6.65964 5.84892 6.76153 5.80928 6.84829 5.74581C6.93505 5.68232 7.00365 5.59721 7.04726 5.49894L8.96486 1.62756C9.01708 1.51481 9.10046 1.41935 9.20518 1.35245C9.30989 1.28555 9.43156 1.25 9.55581 1.25C9.68007 1.25 9.80175 1.28555 9.90646 1.35245C10.0112 1.41935 10.0946 1.51481 10.1468 1.62756Z"
            stroke="#393D3F"
            fill="none"
            strokeWidth="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      )}
      <span className={styles.buttonText}>
        {action === 'hasTickets'
          ? t('eTickets')
          : action === 'hasMobileOrders'
          ? t('Mobile Order')
          : t('Starred')}
      </span>
    </button>
  );
};

export default AttributeFilterButton;
